import { Component } from 'react';
import PropTypes from 'prop-types';

import deepEqual from 'fast-deep-equal';
import { isEmpty } from 'lodash';

import { reduxLocals } from 'utils/redial';
import triggerHooks from './triggerHooks';

// make sure we trigger hooks only when location change is significant (skipping cosmetic URL updates)
const hasAppRouteChanged = (prevLocation, nextLocation) => !deepEqual(prevLocation.pathname, nextLocation.pathname)
  || !deepEqual(prevLocation.query, nextLocation.query)
  || !deepEqual(prevLocation.params, nextLocation.params);

// need to make a copy, so context's router keeps the old data while route loading
// required due to the mutable state of the react-router instance
// TODO this looks like a hack to me, should rather think of better way of doing this...
const freezeProps = (props) => ({
  ...props,
  router: { ...props.router },
  location: { ...props.location },
  params: { ...props.params },
  routes: [...props.routes],
  components: [...props.components],
});

export default class RedialContext extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired, // current location for context, from react-router
    render: PropTypes.func.isRequired, // (renderProps) => children
    onError: PropTypes.func.isRequired, // (err) => {}
    onUpdate: PropTypes.func, // ({loading, deferredLoading, aborted, abort}) => {}
    store: PropTypes.object,

    // redial config
    initial: PropTypes.array, // hooks that must be triggered on the initial render
    blocking: PropTypes.array, // hooks that must be triggered before transition to the next route/component (2nd and onwards)
    deferred: PropTypes.array, // hooks to trigger after transition to route/component completed (async data loading)
    cleanup: PropTypes.array, // hooks to be triggered on old (i.e. no longer active) routes after the transition completed
    timeout: PropTypes.number, // the maximum time (in milliseconds) for the 'blocking' (and 'deferred' in parallel) hooks to finish, otherwise will be aborted, disabled by default
    emptyContentPlaceholder: PropTypes.element, // content to be displayed during the initial load, while there's no content to display
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      loading: false, // whether the blocking hooks are pending
      deferredLoading: false, // whether the deferred hooks are pending
      aborted: () => false, // whether the route transition has been aborted
      abort: () => undefined, // allows aborting the route transition
      prevProps: null, // used to display the previous content while blocking hooks are pending
      nextProps: freezeProps(props), // used to display the content after blocking hooks are finished
      initialLoad: true, // for first render, whether blocking or initial hooks should be triggered
    };
  }

  componentDidMount() {
    this.load(this.props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (hasAppRouteChanged(this.props.location, nextProps.location)) {
      this.load(nextProps);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // while in initial/blocking route transition, keep displaying the previous content
    if (nextState.loading) return false;

    // deferred hooks do not block the render, hence ignore it's state changes and render the content only
    // when initial/blocking hooks finished, but the deferred are about to start
    // NOTE: this is a little trick for performance optimisation, but app should work correctly without it;
    //       feel free to remove it if it start causing issues
    if (!this.state.loading && !nextState.loading
      && this.state.deferredLoading !== nextState.deferredLoading) return false;

    // check only the relevant props and state fields
    return this.props.location !== nextProps.location
      || this.state.loading !== nextState.loading
      || this.state.deferredLoading !== nextState.deferredLoading;
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  notifyOnUpdate() {
    if (this.props.onUpdate) {
      const { loading, deferredLoading, aborted } = this.state;
      this.props.onUpdate({
        loading: loading || false,
        deferredLoading: deferredLoading || false,
        aborted: aborted !== undefined ? aborted() : false,
        abort: this.abort,
      });
    }
  }

  abort() {
    if (this.state.loading || this.state.deferredLoading) {
      this.state.abort();
      this.setState({
        loading: false,
        deferredLoading: false,
      }, this.notifyOnUpdate);
    }
  }

  load(nextProps) {
    let isAbortRequested = false;
    const abortRequested = () => isAbortRequested;
    const abort = () => {
      isAbortRequested = true;
    };

    let isTimeout = false;
    const timeout = () => this.props.timeout && isTimeout;
    const timeoutHandle = (this.props.timeout
      ? setTimeout(() => {
        isTimeout = true;
      }, this.props.timeout)
      : null
    );

    const aborted = () => {
      if (abortRequested()) {
        return 'abort-requested';
      } else if (timeout()) {
        return 'timeout';
      } else if (hasAppRouteChanged(this.props.location, nextProps.location)) {
        return 'app-route-changed';
      }
      return false;
    };

    this.runRouteTransition(
      nextProps,
      abort,
      aborted,
    ).then(() => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
    }).catch((err) => {
      if (timeoutHandle) clearTimeout(timeoutHandle);
      if (!aborted()) this.props.onError(err);
    });
  }

  runRouteTransition(nextProps, abort, aborted) {
    this.setState(prevState => ({
      aborted,
      abort,
      loading: true,
      prevProps: prevState.aborted() ? prevState.prevProps : this.props,
      nextProps: freezeProps(nextProps),
    }), this.notifyOnUpdate);

    // depending whether this is the initial or subsequent load, call the appropriate hooks
    const hooks = this.state.initialLoad
      ? this.props.initial // initial load, some hooks could have been triggered on server
      : this.props.blocking; // prevProps exist - subsequent navigation

    return triggerHooks({
      aborted,
      abort,
      hooks,
      locals: reduxLocals(this.props.store),
      renderProps: nextProps,
    }).then(() => {
      this.completeRouteTransition(nextProps, abort, aborted);
    }).catch((e) => {
      console.log('triggerHooks error:', e); // eslint-disable-line no-console
    });
  }

  completeRouteTransition(nextProps, abort, aborted) {
    if (this.unmounted || aborted()) return Promise.resolve();

    // need to remember prevProps here, as we override it in next operation
    const { prevProps } = this.state;

    this.setState({
      loading: false,
      deferredLoading: true,
      prevProps: null,
      initialLoad: false,
    }, this.notifyOnUpdate);

    // trigger deferred hooks
    return triggerHooks({
      hooks: this.props.deferred,
      locals: reduxLocals(this.props.store),
      renderProps: nextProps,
      abort,
      aborted,
    }).then(() => {
      this.setState({ deferredLoading: false }, this.notifyOnUpdate);
    }).then(() => {
      this.cleanOldRoute(prevProps, nextProps);
    }).catch((e) => {
      console.log('triggerHooks error:', e); // eslint-disable-line no-console
    });
  }

  cleanOldRoute(prevProps, nextProps) {
    if (this.unmounted || !prevProps) return Promise.resolve();

    const unmountedComponents = prevProps.components
      .filter(component => !nextProps.components.includes(component));

    if (unmountedComponents.length) {
      return triggerHooks({
        hooks: this.props.cleanup,
        components: unmountedComponents,
        renderProps: prevProps,
      });
    } else {
      return Promise.resolve();
    }
  }

  render() {
    if (this.state.initialLoad && !isEmpty(this.props.initial)) {
      return this.props.emptyContentPlaceholder;
    }

    try {
      const renderProps = this.state.aborted() ? this.state.prevProps : this.state.nextProps;
      return this.props.render(renderProps);
    } catch (err) {
      this.props.onError(err); // in case of error, no content will keep displayed; onError should handle error logic
      return null;
    }
  }
}
